<template>
  <div class="pa-6 pb-16 d-flex flex-column gap-4" data-testid="varianceanalysis-data-processing-view">
    <div class="d-flex align-center justify-space-between">
      <PageHeader 
        :title="$nova.labelsName('data_processing', 'threshold_label', 'Data Processing')"
        :loading="loading"
        @refresh="refreshData"
      />
      <v-tooltip bottom content-class="tooltip-bottom">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            @click="dataprocessingstatusdialogue = true"
            data-testid="data-processing__status-btn"
          >
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </template>
        <span>Data Processing Status</span>
      </v-tooltip>
    </div>
    <v-card class="pa-3" outlined>
      <v-tabs v-model="activeTab" background-color="transparent" class="data-processing__tabs">
        <v-tab>GL Data</v-tab>
        <v-tab>Drill-down</v-tab>
        <v-tab>Masters</v-tab>
      </v-tabs>
      <v-divider/>
      <v-tabs-items v-model="activeTab" class="pt-4">
        <v-tab-item>
          <data-processing-table :items="glDashboardData" type="gldata" :loading="loading" :show-comparative1="isComparative1Enabled" :show-comparative2="isComparative2Enabled" @row-click="viewDetails" />
        </v-tab-item>
        <v-tab-item>
          <data-processing-table :items="drilldownData" type="drilldown" :loading="loading" :show-comparative1="isComparative1Enabled" :show-comparative2="isComparative2Enabled" @row-click="viewDetails" />
        </v-tab-item>
        <v-tab-item>
          <data-processing-table :items="mastersData" type="masters" :loading="loading" @row-click="viewDetails" />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <div class="">
      <DataProcessingStatusDialog v-if="dataprocessingstatusdialogue" v-model="dataprocessingstatusdialogue" />
    </div>
  </div>
</template>

<script>
import { DATA_PROCESSING } from "../../utils/constants";
import DataProcessingTable from "./components/DataProcessingTable.vue";
import PageHeader from "../../components/PageHeader.vue";
import DataProcessingStatusDialog from "../../components/DataProcessingStatusDialog.vue";
import { mapActions } from "vuex";

const TAB_TYPES = ['gldata', 'drilldown', 'masters'];

export default {
  name: 'varienceanalysis_dataprocessing',
  data: function(){
    return {
      activeTab: 0,
      DATA_PROCESSING,
      loading: false,
      meta: {},
      viewdialog: false,
      viewloading: false,
      viewdata: {},
      colors: [],
      // Per-tab formatted data and loaded tracking
      formatedDataByTab: { gldata: [], drilldown: [], masters: [] },
      loadedTabs: { gldata: false, drilldown: false, masters: false },
      headers: [
        {
          value: "indexno",
          text: "#",
          sortable: false,
          filterable: false,
        },
        {
          type: "date",
          value: "date",
          text: "Batch Date",
          sortable: true,
          filterable: true,
          lock: true,
        },
        {
          value: "__codename",
          text: "Type",
          sortable: true,
          filterable: true,
          lock: true,
        },
        {
          value: "bfcreceivedcount",
          text: "As per BFC (Actual)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "receivedcount",
          text: "As per Databricks (Actual)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "processedcount",
          text: "As per Starlight (Actual)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "all_pbt_sum_actual",
          text: "As per Overall Starlight (Actual)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "bfcreceivedcount_proforma",
          text: "As per BFC (Proforma)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "receivedcount_proforma",
          text: "As per Databricks (Proforma)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "processedcount_proforma",
          text: "As per Starlight (Proforma)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "all_pbt_sum_proforma",
          text: "As per Overall Starlight (Proforma)",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "ignoredcount",
          text: "Starlight Issue",
          type:'number',
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "status",
          text: "Status",
          sortable: true,
          filterable: true,
          lock: true,
        },
        {
          value: "action",
          text: "Action",
          sortable: false,
          datatype: "action",
          lock: true,
          visible: false,
          alignment: "center",
        },
      ],
      dataprocessingstatusdialogue : false,
      batchprocessingstatusdialogue:false,
    }
  },
  components: {
    DataProcessingTable,
    PageHeader,
    DataProcessingStatusDialog
  },
  activated() {
    const type = TAB_TYPES[this.activeTab];
    if (!this.loadedTabs[type]) {
      this.loadTabData(type);
    }
  },
  mounted() {
    this.colors = this.$nova.getColors(10, 0.2);
    const savedTab = sessionStorage.getItem('dataProcessingActiveTab');
    if (savedTab !== null) {
      this.activeTab = Number(savedTab);
    }
    this.loadTabData(TAB_TYPES[this.activeTab]);
  },
  beforeDestroy() {
    sessionStorage.setItem('dataProcessingActiveTab', this.activeTab);
  },
  methods: {
    ...mapActions('varianceanalysis/dataprocessing', ['fetchTabData']),

    refreshData() {
      this.loadTabData(TAB_TYPES[this.activeTab], true);
    },

    async loadTabData(type, force = false) {
      if (!force && this.loadedTabs[type]) return;
      this.loading = true;
      try {
        const rawData = await this.fetchTabData({ type });
        this.$set(this.formatedDataByTab, type, this.formatData(rawData));
        this.$set(this.loadedTabs, type, true);
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.log(err);
      } finally {
        this.loading = false;
      }
    },

    formatData(data = []) {
      let ArrayTemp = [];
      let typelist = {};
      console.log('Raw data for formatting:', data);
      data.forEach((i, k) => {
        let dt = {
          raw: i,
          __key: k,
          __formatedkey: k,
          _id: i._id,
          indexno: k + 1,
          date: this.$nova.formatDateTime(i.date),
          code: i.code,
          bfcreceivedcount: 0,
          bfcreceivedcount_proforma:0,
          bfcreceivedcount_forecast: 0,
          receivedcount: this.$nova.formatNumber(i.mongo_row_count) || 0,
          receivedcount_proforma: this.$nova.formatNumber(i.mongo_row_count_proforma) || 0,
          receivedcount_forecast: this.$nova.formatNumber(i.mongo_row_count_forecast) || 0,
          processedcount: this.$nova.formatNumber(i.processed_line_count) || 0,
          processedcount_proforma: this.$nova.formatNumber(i.processed_line_count_proforma) || 0,
          processedcount_forecast: this.$nova.formatNumber(i.processed_line_count_forecast) || 0,
          bfcreceivedcount_ru: undefined,
          bfcreceivedcount_ru_proforma: undefined,
          bfcreceivedcount_ru_forecast: undefined,
          receivedcount_ru: undefined,
          receivedcount_ru_proforma:undefined,
          receivedcount_ru_forecast: undefined,
          processedcount_ru: undefined,
          processedcount_ru_proforma: undefined,
          processedcount_ru_forecast: undefined,
          ignoredcount: Number(this.$nova.formatNumber(i?.ignored_line_count || 0)) || 0,
          status: DATA_PROCESSING.STATUS.PENDING,
          error_flag: i.error_flag || false,
          all_pbt_sum_actual: i.all_pbt_sum?  Number(this.limitToThreeDecimals(parseFloat(i.all_pbt_sum))) : 'NA',
          all_pbt_sum_proforma: i.all_pbt_sum_proforma?  Number(this.limitToThreeDecimals(parseFloat(i.all_pbt_sum_proforma))) : 'NA',
          all_pbt_sum_forecast: i.all_pbt_sum_forecast?  Number(this.limitToThreeDecimals(parseFloat(i.all_pbt_sum_forecast))) : 'NA',
          all_ru_count: i.all_ru_count || undefined,
          all_ru_count_proforma: i.all_ru_count_proforma || undefined,
          all_ru_count_forecast: i.all_ru_count_forecast || undefined,
        }
        if(!typelist[i.code]) typelist[i.code] = {name: this.getCodeName(i.code), color: this.colors[Object.keys(typelist).length]}
        dt.__codecolor = typelist[i.code].color
        dt.__codename = typelist[i.code].name
        if(DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'master') {
          // source_master_count → As per Source ERP; processed_master_count → As per Flux App
          dt.benchmark = i?.benchmark || '-'
          dt.receivedcount = Number(i?.source_master_count || 0) || 0
          dt.processedcount = Number(i?.processed_master_count || 0) || 0
          dt.unit = '';
        }
        else if(DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'gldashboard') {
          dt.benchmark = i?.benchmark || '-'
          // Source ERP
          dt.bfcreceivedcount = i.source_metrics_total ? Number(this.limitToThreeDecimals(parseFloat(i.source_metrics_total))) : 0
          dt.bfcreceivedcount_proforma = i.source_metrics_total_proforma ? Number(this.limitToThreeDecimals(parseFloat(i.source_metrics_total_proforma))) : 0
          dt.bfcreceivedcount_forecast = 0
          dt.bfcreceivedcount_ru = this.$nova.formatNumber(i.source_ru_count) || 0
          dt.bfcreceivedcount_ru_proforma = this.$nova.formatNumber(i.source_ru_count_proforma) || 0
          dt.bfcreceivedcount_ru_forecast = 0
          // Delta (ETL / Databricks)
          dt.receivedcount = i.delta_metrics_total ? Number(this.limitToThreeDecimals(parseFloat(i.delta_metrics_total))) : 0
          dt.receivedcount_proforma = i.delta_metrics_total_proforma ? Number(this.limitToThreeDecimals(parseFloat(i.delta_metrics_total_proforma))) : 0
          dt.receivedcount_forecast = 0
          dt.receivedcount_ru = this.$nova.formatNumber(i.delta_ru_count) || 0
          dt.receivedcount_ru_proforma = this.$nova.formatNumber(i.delta_ru_count_proforma) || 0
          dt.receivedcount_ru_forecast = 0
          // As per Application (Starlight)
          dt.processedcount = i.process_metrics_total ? Number(this.limitToThreeDecimals(parseFloat(i.process_metrics_total))) : 0
          dt.processedcount_proforma = i.processed_metrics_total_proforma ? Number(this.limitToThreeDecimals(parseFloat(i.processed_metrics_total_proforma))) : 0
          dt.processedcount_forecast = 0
          dt.processedcount_ru = this.$nova.formatNumber(i.process_ru_count) || 0
          dt.processedcount_ru_proforma = this.$nova.formatNumber(i.processed_ru_count_proforma) || 0
          dt.processedcount_ru_forecast = 0
          // Flux App Overall
          dt.all_pbt_sum_actual = i.overall_metrics_total ? Number(this.limitToThreeDecimals(parseFloat(i.overall_metrics_total))) : 'NA'
          dt.all_pbt_sum_proforma = i.overall_metrics_total_proforma ? Number(this.limitToThreeDecimals(parseFloat(i.overall_metrics_total_proforma))) : 'NA'
          dt.all_pbt_sum_forecast = 'NA'
          dt.all_ru_count = i.overall_ru_count || undefined
          dt.all_ru_count_proforma = i.overall_ru_count_proforma || undefined
          dt.all_ru_count_forecast = undefined
          // Popup / detail data
          dt.period_list = i.periods || (i.period_list?.length > 0 ? i.period_list : [])
          dt.ru_list = i.ru_codes || (i.ru_list?.length > 0 ? i.ru_list : [])
          dt.discrepancy_cycles = i.discrepancy_cycles || []
          dt.mismatched_rus = (i.discrepancy_cycles || []).length || undefined
          // Error field (new schema uses 'error' instead of 'error_flag')
          dt.error_flag = i.error || false
          dt.error_message = i.error_message || ''
          dt.unit = ''
        }
        else if(DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'drilldown') {
          dt.benchmark = i?.benchmark || '-'
          // As per parent (outer/parent level amounts)
          dt.bfcreceivedcount = i.total_amount1_parent != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount1_parent))) : 0
          dt.bfcreceivedcount_proforma = i.total_amount2_parent != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount2_parent))) : 0
          dt.bfcreceivedcount_forecast = i.total_amount3_parent != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount3_parent))) : 0
          // As per drill down (sub-ledger level amounts)
          dt.receivedcount = i.total_amount1_sl != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount1_sl))) : 0
          dt.receivedcount_proforma = i.total_amount2_sl != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount2_sl))) : 0
          dt.receivedcount_forecast = i.total_amount3_sl != null ? Number(this.limitToThreeDecimals(parseFloat(i.total_amount3_sl))) : 0
          dt.processedcount = 0
          dt.processedcount_proforma = 0
          dt.processedcount_forecast = 0
          // Popup / detail data (API returns 'periods' and 'ru_codes' for drilldown)
          dt.period_list = i.periods || (i.period_list?.length > 0 ? i.period_list : [])
          dt.ru_list = i.ru_codes || (i.ru_list?.length > 0 ? i.ru_list : [])
          dt.discrepancy_cycles = i.discrepancy_cycles || []
          dt.mismatched_rus = (i.discrepancy_cycles || []).length || undefined
          dt.unit = ''
        }
        // Status computation: use API status code when available
        // 0 = Pending, 1 = Completed, -1 = Failed, 2 = compute from data
        const apiStatus = i.status;
        if (apiStatus === 0) {
          dt.status = DATA_PROCESSING.STATUS.PENDING;
        } else if (apiStatus === 1) {
          dt.status = DATA_PROCESSING.STATUS.COMPLETED;
        } else if (apiStatus === -1) {
          dt.status = DATA_PROCESSING.STATUS.FAILED;
        } else if (apiStatus === 2) {
          // Compute status from data comparison
          if (DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'gldashboard') {
            // Actual: Source ERP Overall = Application (Delta) (numbers)
            //         Flux App Overall = ETL Delta (numbers + RUs)
            const actualEqual = dt.bfcreceivedcount === dt.processedcount &&
              dt.all_pbt_sum_actual === dt.receivedcount &&
              dt.all_ru_count === dt.receivedcount_ru
            // Comparative 1: same checks
            const comp1Equal = dt.bfcreceivedcount_proforma === dt.processedcount_proforma &&
              dt.all_pbt_sum_proforma === dt.receivedcount_proforma &&
              dt.all_ru_count_proforma === dt.receivedcount_ru_proforma
            dt.status = (actualEqual && comp1Equal)
              ? DATA_PROCESSING.STATUS.COMPLETED
              : DATA_PROCESSING.STATUS.PENDING
          }
          else if (DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'drilldown') {
            // Actual: As per Parent GLs = As per Drill-Down
            const actualEqual = dt.bfcreceivedcount === dt.receivedcount
            // Comparative 1: same equality
            const comp1Equal = dt.bfcreceivedcount_proforma === dt.receivedcount_proforma
            dt.status = (actualEqual && comp1Equal)
              ? DATA_PROCESSING.STATUS.COMPLETED
              : DATA_PROCESSING.STATUS.PENDING
          }
          else if (DATA_PROCESSING.PROCESS_CONFIG[dt.code]?.category === 'master') {
            dt.status = (dt.receivedcount === dt.processedcount)
              ? DATA_PROCESSING.STATUS.COMPLETED
              : DATA_PROCESSING.STATUS.PENDING
          }
          else if(dt.code === DATA_PROCESSING.CODES.IMPORT_USER_SECURITY_BULK_UPLOAD && dt.error_flag === false ) dt.status = DATA_PROCESSING.STATUS.COMPLETED
        }
        ArrayTemp.push(dt);
      });
      return ArrayTemp;
    },

    getCodeName(code){
      return DATA_PROCESSING.PROCESS_CONFIG[code]?.name || DATA_PROCESSING.CODE_NAMES[code] || code;
    },
    viewDetails(item){
      sessionStorage.setItem('selectedDataProcessingItem', JSON.stringify(item));
      this.$nova.gotoLink({
        path: `/varianceanalysis/dataprocessing/dataprocessingdetails/${item._id}`
      });
    },
    limitToThreeDecimals(num) {
      if (isNaN(num)) {
          return 0;
      }

      let numString = num.toString();
      let decimalIndex = numString.indexOf('.');

      if (decimalIndex === -1) {
          return num.toFixed(3);
      }

      let integerPart = numString.substring(0, decimalIndex);
      let decimalPart = numString.substring(decimalIndex + 1);
      decimalPart = decimalPart.substring(0, 3);
      let limitedNumString = `${integerPart}.${decimalPart}`;
      return parseFloat(limitedNumString).toFixed(3);
    }
  },
  computed: {
    isComparative2Enabled() {
      return this.$store.state.isComparitive2enabled || false
    },
    isComparative1Enabled() {
      return this.$store.state.isComparitive1enabled || false
    },
    glDashboardData() {
      return this.formatedDataByTab.gldata;
    },
    drilldownData() {
      return this.formatedDataByTab.drilldown;
    },
    mastersData() {
      return this.formatedDataByTab.masters;
    },
  },
  watch: {
    activeTab(newVal) {
      sessionStorage.setItem('dataProcessingActiveTab', newVal);
      this.loadTabData(TAB_TYPES[newVal]);
    },
  },
}
</script>

<style scoped>
/* .subs-icon{
  background-color: var(--v-primary-base) !important;
  color: white;
} */
.tooltip-bottom-right::before{
    left: 53%;
  }

.data-processing__tabs .v-tab {
    text-transform: none;
    font-weight: 400;
    font-size: 16px;
    color: var(--color-primary-base) !important;
    padding: var(--space-2xs) var(--space-md);
}
.data-processing__tabs .v-tab--active {
    color: var(--color-primary-base) !important;
    font-weight: 600;
}
.data-processing__tabs .v-tab:not(.v-tab--active) {
    color: var(--color-tabs-header) !important;
    opacity: 1;
}
::v-deep .data-processing__tabs .v-tabs-bar {
    height: 34px !important ;
}
</style>
